/**
 * AgendaSetupPage — Plano 2 (setup): hub de configuração da agenda nativa.
 *
 * Rota: /admin/agenda/recursos. Gated `scheduling-native` (D-B: gate próprio na página, além
 * da query gateada nos hooks). Esta é a tela que DESTRAVA a agenda nativa: sem recurso/serviço
 * cadastrado o motor de disponibilidade não tem o que computar e a IA não tem o que agendar.
 *
 * Abas: Recursos (peça 1) + Serviços (peça 2). Horário de funcionamento entra como aba na peça 3.
 */
import { CapabilityLockedBanner } from '@lume/ui-components';
import { CalendarCog } from 'lucide-react';
import { useState } from 'react';

import { ResourcesManagerView } from '../features/scheduling-resources/ResourcesManagerView';
import { ServicesManagerView } from '../features/scheduling-resources/ServicesManagerView';
import { WorkingHoursManagerView } from '../features/scheduling-resources/WorkingHoursManagerView';
import { useCapability } from '../hooks/use-capability';
import { useSchedulingResources, useSchedulingServices } from '../hooks/use-scheduling';

import type { JSX } from 'react';

type Tab = 'recursos' | 'servicos' | 'horario';

export function AgendaSetupPage(): JSX.Element {
  const canSchedule = useCapability('scheduling-native');

  // D-B — gate próprio, não confiar só na rota protegida.
  if (!canSchedule) {
    return (
      <div className="flex h-full flex-col p-6">
        <CapabilityLockedBanner
          capability="scheduling-native"
          tierMin="solo"
          fallbackMessage="A agenda nativa não está habilitada para este tenant. Fale com o administrador para ativá-la."
        />
      </div>
    );
  }

  return <AgendaSetupPageInner />;
}

function AgendaSetupPageInner(): JSX.Element {
  const [tab, setTab] = useState<Tab>('recursos');

  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <header
        className="flex items-start gap-3 px-6 py-4"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)' }}
      >
        <CalendarCog size={22} aria-hidden style={{ color: 'var(--fg-2)', marginTop: 2 }} />
        <div>
          <div
            className="text-xs font-medium uppercase"
            style={{ color: 'var(--fg-3)', letterSpacing: 'var(--track-caps)' }}
          >
            Configuração da agenda
          </div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--fg-1)' }}>
            Agenda nativa
          </h1>
          <p className="text-sm" style={{ color: 'var(--fg-3)', marginTop: 2, maxWidth: '60ch' }}>
            A IA só agenda o que estiver configurado aqui — recursos disponíveis, serviços
            oferecidos e horário de funcionamento.
          </p>
        </div>
      </header>

      <nav
        className="flex gap-1 px-6"
        role="tablist"
        aria-label="Configuração da agenda"
        style={{ background: 'var(--bg-surface)', borderBottom: '1px solid var(--border-default)' }}
      >
        <TabButton
          label="Recursos"
          active={tab === 'recursos'}
          onClick={() => setTab('recursos')}
        />
        <TabButton
          label="Serviços"
          active={tab === 'servicos'}
          onClick={() => setTab('servicos')}
        />
        <TabButton label="Horário" active={tab === 'horario'} onClick={() => setTab('horario')} />
      </nav>

      {tab === 'recursos' && <ResourcesTab />}
      {tab === 'servicos' && <ServicesTab />}
      {tab === 'horario' && <WorkingHoursTab />}
    </div>
  );
}

function TabButton({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className="px-3 py-2 text-sm font-medium"
      style={{
        color: active ? 'var(--fg-1)' : 'var(--fg-3)',
        borderBottom: active ? '2px solid var(--brand)' : '2px solid transparent',
      }}
    >
      {label}
    </button>
  );
}

function ResourcesTab(): JSX.Element {
  const resourcesQ = useSchedulingResources(true);
  if (resourcesQ.isLoading) return <Loading what="recursos" />;
  if (resourcesQ.isError) return <LoadError what="recursos" />;
  return <ResourcesManagerView resources={resourcesQ.data?.data ?? []} canManage />;
}

function ServicesTab(): JSX.Element {
  const servicesQ = useSchedulingServices(true);
  if (servicesQ.isLoading) return <Loading what="serviços" />;
  if (servicesQ.isError) return <LoadError what="serviços" />;
  return <ServicesManagerView services={servicesQ.data?.data ?? []} canManage />;
}

function WorkingHoursTab(): JSX.Element {
  const resourcesQ = useSchedulingResources(true);
  if (resourcesQ.isLoading) return <Loading what="recursos" />;
  if (resourcesQ.isError) return <LoadError what="recursos" />;
  return <WorkingHoursManagerView resources={resourcesQ.data?.data ?? []} canManage />;
}

function Loading({ what }: { what: string }): JSX.Element {
  return (
    <p className="px-6 py-5 text-sm" style={{ color: 'var(--fg-3)' }}>
      Carregando {what}…
    </p>
  );
}

function LoadError({ what }: { what: string }): JSX.Element {
  return (
    <p
      className="m-6 rounded-md px-4 py-3 text-sm"
      role="alert"
      style={{
        background: 'var(--bg-surface-2)',
        border: '1px solid var(--border-default)',
        color: 'var(--fg-2)',
      }}
    >
      Falha ao carregar os {what} da agenda.
    </p>
  );
}
